import { Tooltip } from 'antd'
import type { ReactNode } from 'react'

import { formatDateTime } from 'src/utils'

type Props = {
  /**
   * Item carrying revoke metadata. `reason` is not yet on the generated read
   * types, so it's optional here — this is the single place the field is read
   * defensively, so it can be removed in one spot once the serializer exposes
   * it (instead of in every credential table).
   */
  item: { revoked_at?: string | null; reason?: string | null }
  /** The revoked status tag to render (caller controls per-surface styling). */
  children: ReactNode
  /** Tooltip text when a revoke reason is present. */
  reasonTip: (reason: string) => string
  /** Tooltip text (given a formatted timestamp) when only a revoke time exists. */
  atTip: (at: string) => string
}

/**
 * Wraps a revoked-status tag in a tooltip describing why/when it was revoked.
 * Used by the per-app and public-access credential tables so the defensive
 * `reason` read and the reason/timestamp fallback live in one place.
 */
export const RevokedStatusTag = ({
  item,
  children,
  reasonTip,
  atTip
}: Props) => {
  const tip = item.reason
    ? reasonTip(item.reason)
    : item.revoked_at
      ? atTip(formatDateTime(item.revoked_at))
      : undefined

  return tip ? <Tooltip title={tip}>{children}</Tooltip> : <>{children}</>
}
